import * as Sentry from '@sentry/browser';

const SENTRY_TOOLBAR_SCRIPT_ID = 'sentry-toolbar-cdn';

function getSentryClientConfig() {
    const configElement = document.getElementById('classweb-sentry-config');

    if (!configElement || !configElement.textContent) {
        return {};
    }

    try {
        return JSON.parse(configElement.textContent);
    } catch {
        return {};
    }
}

function getSentryToolbarConfig() {
    const clientConfig = getSentryClientConfig();

    return clientConfig.toolbar || null;
}

function getSentryToolbarScriptUrl(version) {
    return (
        'https://browser.sentry-cdn.com/sentry-toolbar/' + encodeURIComponent(version || 'latest') + '/toolbar.min.js'
    );
}

function shouldInitializeSentryToolbar(toolbarConfig) {
    if (!toolbarConfig || !toolbarConfig.enabled) {
        return false;
    }

    if (!toolbarConfig.organizationSlug || !toolbarConfig.projectIdOrSlug) {
        return false;
    }

    const environments = Array.isArray(toolbarConfig.environments) ? toolbarConfig.environments : [];

    return environments.includes(toolbarConfig.currentEnvironment) || toolbarConfig.userIsStaff === true;
}

function loadSentryToolbarScript(scriptUrl) {
    if (window.SentryToolbar) {
        return Promise.resolve(window.SentryToolbar);
    }

    const existingScript = document.getElementById(SENTRY_TOOLBAR_SCRIPT_ID);
    if (existingScript) {
        return new Promise((resolve, reject) => {
            existingScript.addEventListener('load', () => resolve(window.SentryToolbar), { once: true });
            existingScript.addEventListener('error', reject, { once: true });
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = SENTRY_TOOLBAR_SCRIPT_ID;
        script.src = scriptUrl;
        script.crossOrigin = 'anonymous';
        script.referrerPolicy = 'origin';
        script.addEventListener(
            'load',
            () => {
                if (!window.SentryToolbar) {
                    reject(new Error('Sentry Toolbar script loaded without a window.SentryToolbar global.'));
                    return;
                }

                resolve(window.SentryToolbar);
            },
            { once: true }
        );
        script.addEventListener('error', reject, { once: true });
        document.body.appendChild(script);
    });
}

async function initializeSentryToolbar() {
    const toolbarConfig = getSentryToolbarConfig();

    if (!shouldInitializeSentryToolbar(toolbarConfig)) {
        return;
    }

    try {
        const toolbar = await loadSentryToolbarScript(getSentryToolbarScriptUrl(toolbarConfig.version));
        toolbar.init({
            organizationSlug: toolbarConfig.organizationSlug,
            projectIdOrSlug: toolbarConfig.projectIdOrSlug,
            environment: [toolbarConfig.currentEnvironment],
            sentryOrigin: toolbarConfig.sentryOrigin,
            placement: 'bottom-right-corner',
            theme: 'system',
        });
    } catch (error) {
        console.warn('Unable to initialize Sentry Toolbar', error);
    }
}

// Transaction filtering to reduce noise and invalid transactions
function beforeSendTransaction(event) {
    // Filter out health checks and monitoring endpoints
    if (event.transaction) {
        const transactionName = event.transaction.toLowerCase();

        // Skip noisy transactions
        if (
            transactionName.includes('favicon') ||
            transactionName.includes('robots.txt') ||
            transactionName.includes('/_debug_toolbar/') ||
            transactionName.includes('/static/') ||
            transactionName.includes('/media/') ||
            transactionName.includes('/admin/jsi18n/') ||
            transactionName.includes('/admin/autocomplete/')
        ) {
            return null;
        }

        // Filter out very short transactions (likely invalid)
        if (event.spans && event.spans.length === 0) {
            const duration = event.timestamp - event.start_timestamp;
            if (duration < 0.001) {
                // Less than 1ms
                return null;
            }
        }
    }

    return event;
}

// Dynamic sampling function
function tracesSampler(samplingContext) {
    const transactionContext = samplingContext.transactionContext;
    const isDevelopment = process.env.SENTRY_ENVIRONMENT === 'development';

    if (transactionContext && transactionContext.name) {
        const name = transactionContext.name.toLowerCase();

        // Don't sample static files and health checks (even in development)
        if (
            name.includes('favicon') ||
            name.includes('robots.txt') ||
            name.includes('/static/') ||
            name.includes('/media/')
        ) {
            return 0.0;
        }

        // In development, sample everything at 100% for data collection
        if (isDevelopment) {
            return 1.0;
        }

        // Production sampling rates (higher than before for initial data collection)
        // Sample admin pages at moderate rate
        if (name.includes('/admin/')) {
            return 0.5; // Increased from 0.1
        }

        // Sample API endpoints at high rate
        if (name.includes('/api/')) {
            return 0.8; // Increased from 0.3
        }

        // Default sampling for other pages
        return 0.6; // Increased from 0.1
    }

    return isDevelopment ? 1.0 : 0.6;
}

// Only initialize Sentry if a DSN is provided
const sentryDSN = getSentryClientConfig().dsn;
if (sentryDSN) {
    Sentry.init({
        dsn: sentryDSN,
        debug: process.env.SENTRY_ENVIRONMENT === 'development',
        environment: process.env.SENTRY_ENVIRONMENT || 'development',
        release: process.env.SENTRY_RELEASE,
        initialScope: {
            tags: {
                'source.branch': process.env.SENTRY_BRANCH || 'unknown',
            },
        },

        // Enable logs to be sent to Sentry
        enableLogs: true,

        integrations: [
            Sentry.browserTracingIntegration({
                // Configure tracing integration to reduce noise
                enableInp: true,
                enableLcp: true,
                enableFcp: true,
            }),
            Sentry.browserProfilingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
            Sentry.replayCanvasIntegration(),
            // Send console.log, console.warn, and console.error calls as logs to Sentry
            Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
        ],

        // Session Replay sample rates
        replaysSessionSampleRate: process.env.SENTRY_ENVIRONMENT === 'development' ? 0.3 : 0.2,
        replaysOnErrorSampleRate: 1.0,

        // Use dynamic sampling instead of fixed rate
        tracesSampler: tracesSampler,

        // Set trace propagation targets to prevent noise from external resources
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/avservices\.gatech\.edu\/.*/,
            /^https:\/\/.*\.gatech\.edu\/.*/,
        ],

        // Higher profiling rates for initial data collection
        profilesSampleRate: process.env.SENTRY_ENVIRONMENT === 'development' ? 1.0 : 0.3,

        // Transaction filtering
        beforeSendTransaction: beforeSendTransaction,

        // Additional configuration to reduce noise
        beforeSend(event) {
            // Filter out common browser errors that aren't actionable
            if (event.exception) {
                const error = event.exception.values[0];
                if (error && error.type) {
                    // Filter out network errors and script loading errors
                    if (
                        error.type.includes('ChunkLoadError') ||
                        error.type.includes('NetworkError') ||
                        error.type.includes('ResizeObserver')
                    ) {
                        return null;
                    }
                }
            }
            return event;
        },

        // Filter logs to reduce noise
        beforeSendLog(log) {
            // In development, allow more logs for debugging
            if (process.env.SENTRY_ENVIRONMENT === 'development') {
                return log;
            }

            // In production, filter out debug and trace logs
            if (log.level === 'debug' || log.level === 'trace') {
                return null;
            }

            // Filter out common noisy log messages
            if (log.message && typeof log.message === 'string') {
                const message = log.message.toLowerCase();
                if (
                    message.includes('favicon') ||
                    message.includes('robots.txt') ||
                    (message.includes('webpack') && message.includes('hot update'))
                ) {
                    return null;
                }
            }

            return log;
        },
    });
} else {
    console.log('Sentry DSN not provided, skipping Sentry initialization.');
}

// Identify user for error monitoring (see https://security.gatech.edu/terms-of-use)
if (window.SENTRY_USER) {
    Sentry.setUser({
        id: window.SENTRY_USER.id,
        username: window.SENTRY_USER.username,
        email: window.SENTRY_USER.email,
    });
}

initializeSentryToolbar();
